import { useMutation, useQuery, useQueryClient, useSuspenseQuery } from "@tanstack/react-query";
import { BlobContent, ChatAppRequest } from "./models";
import { acquireToken, getAccount } from "../authConfig";
import { base64url } from "./util";

export const BACKEND_URI = import.meta.env.VITE_BACKEND_URI ?? "";
const ME_QUERY_KEY = "me";
const GROUPS_QUERY_KEY = "groups";
export const CONTENT_QUERY_KEY = "content";

interface MsalConfig {
    auth: {
        clientId: string;
        authority: string;
        redirectUri: string;
    };
    cache?: {
        cacheLocation: "sessionStorage" | "localStorage";
        storeAuthStateInCookie: boolean;
    };
}

export async function fetchMsalConfig(): Promise<MsalConfig> {
    const response = await fetch(`${BACKEND_URI}/azure_ad_config`);

    if (!response.ok) {
        throw new Error(`MSAL config response was not ok: ${response.status}`);
    }

    return await response.json();
}

// Get authorization headers for Azure AD tokens
async function getHeaders(notJson = false): Promise<Record<string, string>> {
    const headers: Record<string, string> = {};

    if (!notJson) {
        headers["Content-Type"] = "application/json";
    }

    const account = getAccount();
    if (account) {
        const token = await acquireToken();
        headers["Authorization"] = `Bearer ${token}`;
    }

    return headers;
}

export const useChatMutation = () => {
    return useMutation({
        mutationFn: async (request: ChatAppRequest) => {
            const headers = await getHeaders();
            return await fetch(`${BACKEND_URI}/chat`, {
                method: "POST",
                headers,
                body: JSON.stringify(request)
            });
        }
    });
};

export const useFetchContent = () => {
    return useQuery<Array<BlobContent>>({
        queryKey: [CONTENT_QUERY_KEY],
        queryFn: async () => {
            const headers = await getHeaders();
            return fetch(`${BACKEND_URI}/content`, {
                method: "GET",
                headers
            }).then(res => res.json());
        }
    });
};

/**
 * Fetches teams from the specified endpoint.
 * @param type - "all" for all teams (user + admin), "admin" for admin-only teams
 */
const useFetchTeams = (type: "all" | "admin") => {
    const endpoint = type === "all" ? "/me/groups" : "/me/admin-groups";
    const queryKey = type === "all" ? GROUPS_QUERY_KEY : "admin-groups";

    return useSuspenseQuery<Array<string>>({
        queryKey: [ME_QUERY_KEY, queryKey],
        queryFn: async () => {
            const headers = await getHeaders();
            const teams = await fetch(`${BACKEND_URI}${endpoint}`, {
                method: "GET",
                headers
            }).then(response => response.json());
            // Sort teams alphabetically for consistent ordering
            return teams.sort((a: string, b: string) => a.localeCompare(b));
        },
        staleTime: 5 * 60 * 1000, // 5 minutes - groups don't change often
        gcTime: 10 * 60 * 1000 // 10 minutes - keep in cache
    });
};

/**
 * Fetches all teams (both user and admin teams) for the current user.
 */
export const useFetchGroups = () => useFetchTeams("all");

/**
 * Fetches only admin teams for the current user.
 */
export const useFetchAdminGroups = () => useFetchTeams("admin");

export interface UploadContentBatchResponse {
    batch_id: string;
}

export const useUploadContentBatch = () => {
    return useMutation<UploadContentBatchResponse, Error, { files: File[]; index: string; team: string }>({
        mutationFn: async ({ files, index, team }) => {
            const headers = await getHeaders(true);
            const formData = new FormData();
            files.forEach(file => {
                formData.append("files", file);
            });
            formData.append("collection", index);
            formData.append("group", team);
            return await fetch(`${BACKEND_URI}/content/batch`, {
                method: "POST",
                body: formData,
                headers
            }).then(response => response.json());
        }
    });
};

export const useResolveBatchConflicts = () => {
    return useMutation<{ message: string }, Error, { batchId: string; filenames: string[]; index: string; team: string }>({
        mutationFn: async ({ batchId, filenames, index, team }) => {
            const headers = await getHeaders();
            return await fetch(`${BACKEND_URI}/content/batch/${batchId}/resolve`, {
                method: "POST",
                body: JSON.stringify({ filenames, collection: index, group: team }),
                headers: {
                    ...headers,
                    "Content-Type": "application/json"
                }
            }).then(response => response.json());
        }
    });
};

export const useRetryFailedFiles = () => {
    const queryClient = useQueryClient();
    return useMutation<{ message: string }, Error, { batchId: string; index: string; team: string; filenames?: string[] }>({
        mutationFn: async ({ batchId, index, team, filenames }) => {
            const headers = await getHeaders();
            const body: { collection: string; group: string; filenames?: string[] } = { collection: index, group: team };
            if (filenames) {
                body.filenames = filenames;
            }
            return await fetch(`${BACKEND_URI}/content/batch/${batchId}/retry`, {
                method: "POST",
                body: JSON.stringify(body),
                headers: {
                    ...headers,
                    "Content-Type": "application/json"
                }
            }).then(response => response.json());
        },
        onSuccess: async () => {
            await queryClient.invalidateQueries({ queryKey: [CONTENT_QUERY_KEY] });
        }
    });
};

export const useCancelBatch = () => {
    return useMutation<{ message: string }, Error, { batchId: string }>({
        mutationFn: async ({ batchId }) => {
            const headers = await getHeaders();
            return await fetch(`${BACKEND_URI}/content/batch/${batchId}/cancel`, {
                method: "POST",
                headers
            }).then(response => response.json());
        }
    });
};

export const useRemoveFileFromBatch = () => {
    return useMutation<{ message: string }, Error, { batchId: string; filename: string }>({
        mutationFn: async ({ batchId, filename }) => {
            const headers = await getHeaders();
            return await fetch(`${BACKEND_URI}/content/batch/${batchId}/remove/${encodeURIComponent(filename)}`, {
                method: "DELETE",
                headers
            }).then(response => response.json());
        }
    });
};

export const useGetBatchStatus = (batchId: string | null) => {
    return useQuery({
        queryKey: ["batch", batchId],
        queryFn: async () => {
            if (!batchId) return null;
            const headers = await getHeaders();
            const response = await fetch(`${BACKEND_URI}/content/batch/${batchId}`, {
                headers
            });
            if (!response.ok) {
                if (response.status === 404) return null;
                throw new Error("Failed to fetch batch status");
            }
            return response.json();
        },
        enabled: !!batchId,
        refetchInterval: false
    });
};

export interface BatchDeleteArgs {
    files: Array<{
        filename: string;
        collections: string[];
    }>;
}

export const useBatchDeleteContent = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async ({ files }: BatchDeleteArgs) => {
            const headers = await getHeaders();
            const response = await fetch(`${BACKEND_URI}/content/batch-delete`, {
                method: "POST",
                headers,
                body: JSON.stringify({ files })
            });

            if (!response.ok) {
                throw new Error(`Batch delete failed: ${response.statusText}`);
            }

            return await response.json();
        },
        onSuccess: async () => {
            await queryClient.invalidateQueries({ queryKey: [CONTENT_QUERY_KEY] });
        }
    });
};

export interface GenerateSecureContentUrlArgs {
    path: string;
    filehash?: string | undefined;
    collection?: string | undefined;
}

export const useGenerateSecureContentUrl = () => {
    return useMutation({
        mutationFn: async (
            args: GenerateSecureContentUrlArgs
        ): Promise<{
            url: string;
        }> => {
            const headers = await getHeaders();
            return await fetch(`${BACKEND_URI}/content/actions/generate-sas`, {
                method: "POST",
                headers,
                body: JSON.stringify(args)
            }).then(response => {
                if (!response.ok) {
                    throw new Error("Failed to generate SAS token");
                }
                return response.json();
            });
        }
    });
};

export const useUserGuideUrl = () => {
    return useMutation({
        mutationFn: async (): Promise<{ url: string }> => {
            const headers = await getHeaders();
            const response = await fetch(`${BACKEND_URI}/content/docs/user-guide`, {
                method: "GET",
                headers
            });
            if (!response.ok) {
                throw new Error("Failed to fetch user guide URL");
            }
            return response.json();
        }
    });
};

export function getCitationFilePath(citation: string, collection: string): string {
    return `${BACKEND_URI}/content/actions/generate-sas?path=${base64url(citation)}&collection=${base64url(collection)}`;
}
